import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../services/api';
import { EmptyState, Meter, Section, Stat } from './parts';

interface ProgressRow {
  enrollmentId: string;
  membershipId: string;
  courseId: string;
  courseTitle: string;
  status: string;
  progressPercent: number;
  lastSeenAt: string | null;
}

/**
 * A trainer or facilitator: who is on my courses and how far have they got.
 *
 * `GET /v1/learn/progress` is the same endpoint the school admin and the
 * ministry analyst call. What comes back differs because RLS scopes it from
 * their grant — so this component does not know or care how wide its own view
 * is, which is what stops "which dashboard am I" from leaking into every query.
 */
export function FacilitatorDashboard() {
  const { orgSlug = '' } = useParams();

  const { data, isLoading } = useQuery({
    queryKey: ['org-progress'],
    queryFn: () => apiClient.get<ProgressRow[]>('/v1/learn/progress'),
  });

  const rows = data ?? [];
  const completed = rows.filter(row => row.status === 'completed').length;
  const stalled = rows.filter(
    row =>
      row.status === 'active' &&
      row.progressPercent < 100 &&
      row.lastSeenAt !== null &&
      Date.now() - Date.parse(row.lastSeenAt) > 14 * 86_400_000,
  );

  return (
    <div>
      <h1 className="text-2xl font-bold text-ink-900">My cohorts</h1>
      <p className="text-sm text-ink-600">Who is learning, and who has stopped.</p>

      <div className="mt-5 grid gap-4 sm:grid-cols-3">
        <Stat label="Enrolments" value={rows.length} />
        <Stat label="Completed" value={completed} tone="good" />
        <Stat
          label="Stalled"
          value={stalled.length}
          tone={stalled.length > 0 ? 'warn' : 'default'}
          hint="No activity for two weeks"
        />
      </div>

      <Section title="Progress" action={{ label: 'Courses', to: `/o/${orgSlug}/courses` }}>
        {isLoading && <p className="text-sm text-ink-500">Loading…</p>}
        {!isLoading && rows.length === 0 && (
          <EmptyState>
            Nobody is enrolled yet. Assign a course from the catalogue to get started.
          </EmptyState>
        )}

        {rows.length > 0 && (
          <div className="card overflow-x-auto p-0">
            <table className="w-full text-sm">
              <thead className="bg-ink-100 text-left text-xs uppercase tracking-wide text-ink-500">
                <tr>
                  <th className="px-4 py-2">Course</th>
                  <th className="px-4 py-2">Progress</th>
                  <th className="px-4 py-2">Status</th>
                  <th className="px-4 py-2">Last seen</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ink-100">
                {rows.slice(0, 50).map(row => (
                  <tr key={row.enrollmentId}>
                    <td className="px-4 py-2">{row.courseTitle}</td>
                    <td className="px-4 py-2">
                      <Meter value={row.progressPercent} />
                    </td>
                    <td className="px-4 py-2 text-xs capitalize text-ink-600">{row.status}</td>
                    <td className="px-4 py-2 text-xs text-ink-500">
                      {row.lastSeenAt ? new Date(row.lastSeenAt).toLocaleDateString() : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>
    </div>
  );
}
